import { GET, POST, PUT, DELETE } from './executer';
import apiServiceInstance from './apiService';

/**
 * Event data source type
 */
export type EventSource = 'prod' | 'legacy' | 'demo';

/**
 * Circuit information (subset from circuits table)
 */
export interface CircuitInfo {
  display_name: string;
  country: string;
  time_zone: string;
  latitude?: number;
  longitude?: number;
}

/**
 * Weather station information for an event
 */
export interface Station {
  reference: string; // Station ID within the event (e.g., "1", "2", "3")
  lat: number;
  lon: number;
  altitude: number;
  nearest_segment_direction: number;
}

/**
 * GeoJSON Polygon for radar bounding box
 */
export interface RadarBbox {
  type: 'Polygon';
  coordinates: number[][][];
}

/**
 * Radar period with start and end times
 */
export interface RadarPeriod {
  start_time: string; // UTC timestamp (e.g., "2025-11-06 13:00:00.000000 UTC")
  end_time: string;   // UTC timestamp (e.g., "2025-11-06 19:00:00.000000 UTC")
}

/**
 * Session within an event
 */
export interface Session {
  session_name: string;
  start_time: string; // ISO 8601 timestamp
  end_time: string;   // ISO 8601 timestamp
  description: string | null;
}

/**
 * Event entity representing an F1 race event
 */
export interface Event {
  season_year: number;
  circuit_id: string;
  name: string;
  start_date: string; // ISO date format YYYY-MM-DD
  end_date: string;   // ISO date format YYYY-MM-DD
  legacy: boolean;
  demo: boolean;
  overview_summary?: string | null;
  stations: Station[];
  sessions: Session[]; // Sessions embedded within the event
  radar_bbox?: RadarBbox | null; // GeoJSON Polygon for radar bounding box
  radar_periods?: RadarPeriod[]; // Radar coverage periods
  observations_enabled?: boolean | null; // If false, observations features are disabled (forecasting-only mode)
  circuit_geojson_id?: string | null; // Optional: custom GeoJSON file ID for this event (defaults to circuit_id if not set)
}

/**
 * Event with circuit details and dynamic status
 */
export interface EventWithCircuit extends Event {
  status: 'Past' | 'Current' | 'Future';
  circuit: CircuitInfo;
}

/**
 * Request payload for creating a new event
 */
export interface CreateEventRequest {
  season_year: number;
  circuit_id: string;
  name: string;
  start_date: string; // ISO date format YYYY-MM-DD
  end_date: string;   // ISO date format YYYY-MM-DD
  legacy: boolean;
  demo: boolean;
  overview_summary?: string | null;
  stations?: Station[];
  sessions?: Session[]; // Optional: sessions for the event
  radar_bbox?: RadarBbox | null; // GeoJSON Polygon for radar bounding box
  radar_periods?: RadarPeriod[];
  observations_enabled?: boolean | null; // Optional: if false, observations features are disabled
  circuit_geojson_id?: string | null; // Optional: custom GeoJSON file ID for this event
}

/**
 * Request payload for updating an event
 */
export interface UpdateEventRequest {
  name?: string;
  start_date?: string; // ISO date format YYYY-MM-DD
  end_date?: string;   // ISO date format YYYY-MM-DD
  legacy?: boolean;
  demo?: boolean;
  overview_summary?: string | null;
  stations?: Station[];
  sessions?: Session[]; // Optional: update sessions for the event
  radar_bbox?: RadarBbox | null; // GeoJSON Polygon for radar bounding box
  radar_periods?: RadarPeriod[];
  observations_enabled?: boolean | null; // Optional: update observations enabled flag
  circuit_geojson_id?: string | null; // Optional: update custom GeoJSON file ID
}

class EventsService {
  /**
   * Fetch all events from the backend
   * @param seasonYear Optional season year to filter events
   */
  async getAllEvents(seasonYear?: number) {
    const url = seasonYear
      ? `/api/events?season_year=${seasonYear}`
      : '/api/events';

    return await apiServiceInstance.client.execute<EventWithCircuit[]>(
      GET,
      url
    );
  }

  /**
   * Fetch the current event - one where the current date falls between start_date and end_date.
   * Returns null if no event is currently active.
   */
  async getCurrentEvent() {
    const response = await apiServiceInstance.client.execute<EventWithCircuit>(
      GET,
      '/api/events/current'
    );
    if (response.success && response.data) {
      return response.data;
    }
    return null;
  }

  /**
   * Fetch a specific event by season, circuit, and start date
   */
  async getEvent(seasonYear: number, circuitId: string, startDate: string) {
    return await apiServiceInstance.client.execute<EventWithCircuit>(
      GET,
      `/api/events/${seasonYear}/${circuitId}/${startDate}`
    );
  }

  /**
   * Create a new event
   * Requires: root role
   * @param eventData Event data to create
   * @param userToken User authentication token (not API key)
   */
  async createEvent(eventData: CreateEventRequest, userToken: string) {
    return await apiServiceInstance.client.execute<EventWithCircuit>(
      POST,
      '/api/events',
      {
        data: eventData,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Update an existing event
   * Requires: root role
   * @param seasonYear Season year
   * @param circuitId Circuit ID
   * @param startDate Start date (YYYY-MM-DD)
   * @param updates Partial event data to update
   * @param userToken User authentication token (not API key)
   */
  async updateEvent(
    seasonYear: number,
    circuitId: string,
    startDate: string,
    updates: UpdateEventRequest,
    userToken: string
  ) {
    return await apiServiceInstance.client.execute<EventWithCircuit>(
      PUT,
      `/api/events/${seasonYear}/${circuitId}/${startDate}`,
      {
        data: updates,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Delete an event
   * Requires: root role
   * @param seasonYear Season year
   * @param circuitId Circuit ID
   * @param startDate Start date (YYYY-MM-DD)
   * @param userToken User authentication token (not API key)
   */
  async deleteEvent(
    seasonYear: number,
    circuitId: string,
    startDate: string,
    userToken: string
  ) {
    return await apiServiceInstance.client.execute<{ message: string }>(
      DELETE,
      `/api/events/${seasonYear}/${circuitId}/${startDate}`,
      {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }
}

const eventsServiceInstance = new EventsService();
export default eventsServiceInstance;

